import type {
  IconProp,
  IconDefinition,
  SizeProp,
} from '@fortawesome/fontawesome-svg-core'
import { VariantProps } from 'class-variance-authority'
import {
  FontAwesomeIcon,
  FontAwesomeIconProps,
} from '@fortawesome/react-fontawesome'
import * as React from 'react'

import { mtc } from '../../../utils/style-helpers'
import { backgroundStyles, styles } from './styles'

export interface IconProps extends Omit<FontAwesomeIconProps, 'icon'> {
  /** The background color of the icon */
  backgroundColor?: VariantProps<typeof backgroundStyles>['backgroundColor']
  /** The shape of the background */
  backgroundStyle?: 'circle' | 'rounded'
  backgroundClassName?: string
  /** Icon color - Use Tailwind utility class names with text-* prefix */
  color?: string // will need to come back to this and be more explicit
  /** Icon to display - can be an IconDefinition or a FontAwesome icon object */
  icon: IconProp | IconDefinition
  /** Font Awesome supports t-shirt size scale from 2xs to 10x as well as literal sizing from 1x to 10x */
  size?: SizeProp
  /** Variant of the icon */
  variant?: 'checkbox'
}

/** Icon component that wraps FontAwesomeIcon */
export const Icon = React.forwardRef<HTMLSpanElement, IconProps>(
  (
    {
      backgroundColor,
      backgroundStyle,
      className,
      backgroundClassName,
      color = 'text-neutral-600',
      icon,
      size,
      variant,
      onClick,
      style,
      ...props
    },
    ref
  ) => {
    const hasOnClick = Boolean(onClick)

    const handleClick = (e: React.MouseEvent<HTMLSpanElement>) => {
      if (onClick) {
        onClick(e as unknown as React.MouseEvent<SVGSVGElement>)
      }
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>) => {
      if (onClick && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault()
        onClick(e as unknown as React.MouseEvent<SVGSVGElement>)
      }
    }

    return (
      <span
        ref={ref}
        className={mtc(
          backgroundStyles({ backgroundStyle, size, backgroundColor, variant }),
          backgroundClassName
        )}
        onClick={hasOnClick ? handleClick : undefined}
        onKeyDown={hasOnClick ? handleKeyDown : undefined}
        role={hasOnClick ? 'button' : undefined}
        tabIndex={hasOnClick ? 0 : undefined}
      >
        <FontAwesomeIcon
          className={mtc(styles({ hasOnClick }), className, color)}
          icon={icon}
          size={size}
          style={size ? style : { fontSize: 'inherit', ...style }}
          {...props}
        />
      </span>
    )
  }
)

Icon.displayName = 'Icon'
